import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Home, TrendingUp, TrendingDown, BarChart2, PieChart, Download, Calendar, ArrowUpRight } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const keyIndicators = [{
  label: 'Agriculture GDP Contribution',
  value: '24.1%',
  change: '+1.2%',
  trend: 'up',
  period: 'FY 2022/23'
}, {
  label: 'Total Crop Production',
  value: '32.4M MT',
  change: '+4.8%',
  trend: 'up',
  period: 'FY 2022/23'
}, {
  label: 'Coffee Exports',
  value: '$849M',
  change: '+12.3%',
  trend: 'up',
  period: '2023'
}, {
  label: 'Fish Production',
  value: '562K MT',
  change: '-2.1%',
  trend: 'down',
  period: '2023'
}, {
  label: 'Livestock Population',
  value: '82.3M',
  change: '+3.5%',
  trend: 'up',
  period: '2023 Census'
}, {
  label: 'Agric. Employment Share',
  value: '68%',
  change: '-1.0%',
  trend: 'down',
  period: '2023'
}];
const reports = [{
  title: 'Annual Agricultural Sector Performance Report FY 2022/23',
  type: 'Annual Report',
  date: 'October 2023',
  size: '8.7 MB'
}, {
  title: 'Quarterly Agricultural Production Statistics - Q4 2023',
  type: 'Quarterly Report',
  date: 'January 2024',
  size: '3.2 MB'
}, {
  title: 'Uganda Census of Agriculture 2023 - Preliminary Results',
  type: 'Census Data',
  date: 'March 2024',
  size: '12.4 MB'
}, {
  title: 'Monthly Market Price Bulletin - April 2024',
  type: 'Market Data',
  date: 'April 2024',
  size: '1.8 MB'
}, {
  title: 'Coffee Sub-Sector Export Statistics 2023',
  type: 'Trade Data',
  date: 'February 2024',
  size: '2.5 MB'
}, {
  title: 'Livestock Disease Surveillance Report 2023',
  type: 'Surveillance Report',
  date: 'December 2023',
  size: '4.1 MB'
}];
const commodityData = [{
  commodity: 'Maize',
  production: '3.8M MT',
  area: '2.1M Ha',
  yield: '1.8 MT/Ha',
  trend: 'up'
}, {
  commodity: 'Beans',
  production: '1.1M MT',
  area: '0.9M Ha',
  yield: '1.2 MT/Ha',
  trend: 'up'
}, {
  commodity: 'Cassava',
  production: '5.2M MT',
  area: '1.4M Ha',
  yield: '3.7 MT/Ha',
  trend: 'up'
}, {
  commodity: 'Rice',
  production: '0.38M MT',
  area: '0.18M Ha',
  yield: '2.1 MT/Ha',
  trend: 'up'
}, {
  commodity: 'Coffee',
  production: '6.2M bags',
  area: '0.42M Ha',
  yield: '14.8 bags/Ha',
  trend: 'up'
}, {
  commodity: 'Milk',
  production: '2.8B Litres',
  area: 'N/A',
  yield: '6.2 L/cow/day',
  trend: 'up'
}];
export function SectorStatisticsPage() {
  return <>
      <Header data-id="element-1483" />
      <main className="flex-grow pt-[160px] md:pt-[148px] bg-gray-50 pb-20" data-id="element-1484">
        {/* Hero */}
        <div className="relative text-white py-32 md:py-48 overflow-hidden" data-id="element-1485">
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/hWR9G9c2X42CMKZV1a5QSs/fdhjkl_(11).jpg")'
        }} data-id="element-1486"></div>
          <div className="absolute inset-0 bg-uganda-green/80" data-id="element-1487"></div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-8" data-id="element-1488">
            <div className="flex items-center text-sm text-green-100 mb-6" data-id="element-1489">
              <Link to="/" className="hover:text-white flex items-center" data-id="element-1490">
                <Home className="w-4 h-4 mr-1" data-id="element-1491" /> Home
              </Link>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-1492" />
              <span data-id="element-1493">Data & Statistics</span>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-1494" />
              <span className="text-white font-medium" data-id="element-1495">Sector Statistics</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-1496">
              Sector Statistics
            </h1>
            <p className="text-lg text-green-50 max-w-3xl leading-relaxed" data-id="element-1497">
              Comprehensive agricultural data, production statistics, trade
              figures, and market information for Uganda's agriculture sector.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-12" data-id="element-1498">
          {/* Key Indicators */}
          <h2 className="text-2xl font-bold text-uganda-black mb-6" data-id="element-1499">
            Key Indicators
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12" data-id="element-1500">
            {keyIndicators.map((indicator, index) => <motion.div key={indicator.label} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: index * 0.06
          }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200" data-id="element-1501">
                <p className="text-sm text-gray-500 mb-2" data-id="element-1502">{indicator.label}</p>
                <div className="flex items-end justify-between" data-id="element-1503">
                  <p className="text-3xl font-bold text-uganda-black" data-id="element-1504">
                    {indicator.value}
                  </p>
                  <div className={`flex items-center gap-1 text-sm font-semibold ${indicator.trend === 'up' ? 'text-green-600' : 'text-red-500'}`} data-id="element-1505">
                    {indicator.trend === 'up' ? <TrendingUp className="w-4 h-4" data-id="element-1506" /> : <TrendingDown className="w-4 h-4" data-id="element-1507" />}
                    {indicator.change}
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2" data-id="element-1508">{indicator.period}</p>
              </motion.div>)}
          </div>

          {/* Commodity Production Table */}
          <h2 className="text-2xl font-bold text-uganda-black mb-6" data-id="element-1509">
            Commodity Production Overview
          </h2>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-12" data-id="element-1510">
            <div className="overflow-x-auto" data-id="element-1511">
              <table className="w-full" data-id="element-1512">
                <thead data-id="element-1513">
                  <tr className="bg-gray-50 border-b border-gray-200" data-id="element-1514">
                    <th className="text-left px-6 py-4 text-sm font-bold text-gray-700 uppercase tracking-wider" data-id="element-1515">
                      Commodity
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-bold text-gray-700 uppercase tracking-wider" data-id="element-1516">
                      Production
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-bold text-gray-700 uppercase tracking-wider hidden md:table-cell" data-id="element-1517">
                      Area Harvested
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-bold text-gray-700 uppercase tracking-wider hidden md:table-cell" data-id="element-1518">
                      Avg Yield
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-bold text-gray-700 uppercase tracking-wider" data-id="element-1519">
                      Trend
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-1520">
                  {commodityData.map((item, i) => <tr key={item.commodity} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`} data-id="element-1521">
                      <td className="px-6 py-4 font-semibold text-uganda-black" data-id="element-1522">
                        {item.commodity}
                      </td>
                      <td className="px-6 py-4 text-gray-700" data-id="element-1523">
                        {item.production}
                      </td>
                      <td className="px-6 py-4 text-gray-700 hidden md:table-cell" data-id="element-1524">
                        {item.area}
                      </td>
                      <td className="px-6 py-4 text-gray-700 hidden md:table-cell" data-id="element-1525">
                        {item.yield}
                      </td>
                      <td className="px-6 py-4" data-id="element-1526">
                        <span className="inline-flex items-center gap-1 text-green-600 text-sm font-medium" data-id="element-1527">
                          <ArrowUpRight className="w-4 h-4" data-id="element-1528" /> Growing
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>

          {/* Downloadable Reports */}
          <h2 className="text-2xl font-bold text-uganda-black mb-6" data-id="element-1529">
            Statistical Reports & Publications
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-1530">
            {reports.map((report, index) => <motion.div key={report.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: index * 0.06
          }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 flex flex-col" data-id="element-1531">
                <div className="flex items-start gap-3 mb-4" data-id="element-1532">
                  <div className="w-10 h-10 rounded-lg bg-green-50 text-uganda-green flex items-center justify-center flex-shrink-0" data-id="element-1533">
                    <BarChart2 className="w-5 h-5" data-id="element-1534" />
                  </div>
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider bg-gray-100 px-2 py-1 rounded" data-id="element-1535">
                    {report.type}
                  </span>
                </div>
                <h3 className="font-bold text-lg text-uganda-black mb-3 flex-grow leading-snug" data-id="element-1536">
                  {report.title}
                </h3>
                <div className="flex items-center gap-3 text-xs text-gray-500 mb-4" data-id="element-1537">
                  <span className="flex items-center gap-1" data-id="element-1538">
                    <Calendar className="w-3 h-3" data-id="element-1539" /> {report.date}
                  </span>
                  <span className="w-1 h-1 bg-gray-300 rounded-full" data-id="element-1540" />
                  <span data-id="element-1541">{report.size}</span>
                </div>
                <button className="w-full flex items-center justify-center gap-2 bg-white border border-gray-200 hover:border-uganda-green hover:text-uganda-green text-gray-700 py-2.5 rounded-lg text-sm font-semibold transition-colors" data-id="element-1542">
                  <Download className="w-4 h-4" data-id="element-1543" /> Download
                </button>
              </motion.div>)}
          </div>
        </div>
      </main>
      <Footer data-id="element-1544" />
    </>;
}